import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Circle, Flex, HStack, Text } from '@chakra-ui/react';

import colors from '../../../styles/theme/foundation/colors';
import CertificateCreationBlueprintType from '../../../types/CertificateCreationBlueprintType';
import DropdownMenu from '../../DropdownMenu';

import DocumentViewer from './DocumentViewer';
import PolicyFormGenerator from './PolicyFormGenerator';

const DocumentDropdownMenu = ({
  currentFile,
  allDocuments,
  onSelect
}) => {
  if (!allDocuments.length) return null;

  const options = Object.values(allDocuments)
    .map((file, index) => ({
      id: index,
      label: file.name
    }));

  return (
    <DropdownMenu
      isDisabled={allDocuments.length === 1}
      options={options}
      selected={currentFile?.name}
      variant="secondary"
      onSelect={onSelect}
    />
  );
};

const ReviewStep = ({ blueprint }) => {
  const dispatch = useDispatch();
  const contentHeight = useSelector(blueprint.selectContentHeight);
  const allDocuments = useSelector(blueprint.selectAllDocuments);
  const currentFile = useSelector(blueprint.selectCurrentFile);
  let requirementPolicyTypes = useSelector(blueprint.selectRequirementPolicyTypes);
  const selectedAdditionalPolicyTypes = useSelector(blueprint.selectSelectedAdditionalPolicyTypes);
  const selectedPolicyTypes = useSelector(blueprint.selectSelectedPolicyTypes);
  const currentPolicyTypeFromSelected = useSelector(blueprint.selectCurrentPolicyTypeFromSelected);
  const currentPolicyTypeFromRequirements = useSelector(blueprint.selectCurrentPolicyTypeFromRequirements);
  const { companyId } = useSelector(blueprint.selectCompany);
  const policyData = useSelector(blueprint.selectPolicyData);
  const { chooseAnyPolicyType } = useSelector(blueprint.selectOptions);

  requirementPolicyTypes = [
    ...requirementPolicyTypes,
    ...selectedAdditionalPolicyTypes
  ];

  const policyType = useMemo(
    () => (chooseAnyPolicyType ? currentPolicyTypeFromSelected : currentPolicyTypeFromRequirements),
    [chooseAnyPolicyType, currentPolicyTypeFromRequirements, currentPolicyTypeFromSelected]
  );

  const policyTypeList = useMemo(
    () => (chooseAnyPolicyType ? selectedPolicyTypes : requirementPolicyTypes),
    [chooseAnyPolicyType, selectedPolicyTypes, requirementPolicyTypes]
  );

  const policyDocuments = useMemo(
    () => (allDocuments.filter(doc => doc.linkedPolicyTypeIds.includes(policyType.id))),
    [allDocuments, policyType]
  );

  const handleSubmit = data => {
    dispatch(blueprint.setPolicyData(policyType.id, data));

    if (policyType === policyTypeList[policyTypeList.length - 1]) {
      dispatch(blueprint.nextStep());
    } else {
      dispatch(blueprint.nextPolicyType());
    }
  };

  const handleBackButton = data => {
    dispatch(blueprint.setPolicyData(policyType.id, data));

    if (policyType !== policyTypeList[0] && policyTypeList.length > 1) {
      dispatch(blueprint.previousPolicyType());
    } else {
      dispatch(blueprint.goBack(2));
    }
  };

  const handleSkipButton = () => {
    dispatch(blueprint.skipPolicyType(policyType.id));

    if (policyType === policyTypeList[policyTypeList.length - 1]) {
      dispatch(blueprint.nextStep());
    } else {
      dispatch(blueprint.nextPolicyType());
    }
  };

  const buttonLabels = useMemo(() => {
    const endOfPolicyTypeList = policyType === policyTypeList[policyTypeList.length - 1];

    if (chooseAnyPolicyType) {
      return {
        submitButtonLabel: endOfPolicyTypeList ? 'Review Submission' : 'Next Policy',
        secondaryButtonLabel: 'Back',
        ternaryButtonLabel: null
      };
    }

    return {
      submitButtonLabel: endOfPolicyTypeList ? 'Review Submission' : 'Next Policy',
      secondaryButtonLabel: 'Back',
      ternaryButtonLabel: endOfPolicyTypeList ? 'Skip and Review' : 'Skip'
    };
  }, [chooseAnyPolicyType, policyType, policyTypeList]);

  useEffect(() => {
    if (policyDocuments.length) {
      // if policyDocuments exist, set current file to the first document the policy was linked to
      dispatch(blueprint.setCurrentFile(allDocuments.indexOf(policyDocuments[0])));
    } else if (!currentFile && allDocuments.length) {
      // if we haven't set a current file yet and documents have been prepared, set the current file to the first document
      dispatch(blueprint.setCurrentFile(0));
    }
  }, [allDocuments, policyDocuments]);

  return (
    <Flex flexBasis="100%" flexFlow="row nowrap" overflow="auto" width="100%">
      <Flex backgroundColor="white" flexBasis="40%" flexDir="column" p={6}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text textStyle="displayXs">Review Policies</Text>
          <HStack>
            {policyTypeList.length > 1 && policyTypeList.map(policy => (
              <Circle
                bg={colors.primaryScale['600']}
                h="10px"
                opacity={policy.id === policyType.id ? 1 : 0.45}
                w="10px"
              />
            ))}
          </HStack>
        </Flex>
        <PolicyFormGenerator
          blueprint={blueprint}
          companyId={companyId}
          formName={`${policyType.id}-submitPolicyData`}
          key={policyType.id}
          policy={policyData[policyType.id]?.savedPolicyData || null}
          policyType={policyType}
          secondaryButtonLabel={buttonLabels.secondaryButtonLabel}
          secondaryFunction={data => handleBackButton(data)}
          submitButtonLabel={buttonLabels.submitButtonLabel}
          submitFunction={data => handleSubmit(data)}
          ternaryButtonLabel={buttonLabels.ternaryButtonLabel}
          ternaryFunction={data => handleSkipButton(data)}
        />
      </Flex>

      <Flex backgroundColor="backgroundScale.50" flexBasis="60%" p="3rem">
        {!currentFile && (
          <Flex
            alignItems="center"
            flexBasis="100%"
            justifyContent="center"
            minH={contentHeight}
            textAlign="center"
          >
            <Text>{`No documents uploaded for ${policyType.displayName}`}</Text>
          </Flex>
        )}
        {allDocuments && currentFile && (
          <Flex
            alignItems="flex-start"
            flexBasis="100%"
            justifyContent="center"
            minH={contentHeight}
            textAlign="center"
          >
            <Box maxHeight="100%" minH={contentHeight} pt={4} width="100%">
              <DocumentViewer
                file={currentFile}
                leftHeaderChildren={(
                  <DocumentDropdownMenu
                    allDocuments={allDocuments}
                    blueprint={blueprint}
                    currentFile={currentFile}
                    onSelect={selected => {
                      const selectedValue = parseInt(selected.value, 10);
                      dispatch(blueprint.setCurrentFile(selectedValue));
                    }}
                  />
                )}
                maxHeight="85vh"
              />
            </Box>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

ReviewStep.propTypes = { blueprint: CertificateCreationBlueprintType };

export default ReviewStep;
